/**
 * Centrifugo connection-token provider.
 *
 * Centrifugo authenticates each WebSocket connection with a JWT signed by the
 * server's `token_hmac_secret_key`. That secret is SERVER-SIDE ONLY and must
 * never be shipped in the frontend bundle — so the browser obtains the JWT from
 * the livechat backend token endpoint (APICC-2476):
 *
 *     GET livechat/v1/core/centrifugo/token   (Authorization: Bearer <session token>)
 *     → { success: true, data: { token: "<jwt>" } }
 *
 * The caller's session JWT must carry `permissions.livechat == true`; otherwise
 * the endpoint returns 403.
 *
 * TOKEN REFRESH:
 * Centrifugo tokens are short-lived (~50 min). centrifuge-js automatically
 * re-invokes this function before the current token expires — no manual timer
 * is required. Because this function fetches a NEW token from the API on each
 * call, refresh "just works": the SDK calls getCentrifugoToken() again, gets a
 * fresh JWT, and keeps the connection alive.
 *
 * TEMPORARY dev fallback:
 * A pre-generated token can be supplied via `VUE_APP_CENTRIFUGO_DEV_TOKEN`
 * (from the team's generate_token.py) for local dev when the API path isn't
 * usable (e.g. a session without livechat permission). NOTE this dev token is
 * STATIC and does NOT refresh — once it expires (~50 min) the connection drops
 * and won't recover until the page reloads. Never set it in staging/production.
 */
import { environment } from '@/shared/env'
import apiInterceptor from '@/shared/apiInterceptor'

/** Token endpoint path (relative to VUE_APP_API_URL). Per APICC-2476. */
const CENTRIFUGO_TOKEN_PATH = 'livechat/v1/core/centrifugo/token'

/**
 * Returns a fresh Centrifugo connection JWT from the livechat token endpoint.
 *
 * apiInterceptor attaches the session Bearer token and unwraps the response
 * envelope, so a successful call resolves to the inner { token } object.
 *
 * centrifuge-js contract: returning '' (empty string) is a valid "no token"
 * result and does NOT force a disconnect — used here as a soft failure so the
 * client doesn't throw on startup when the token can't be fetched.
 *
 * @returns {Promise<string>} a JWT string, or '' when unavailable.
 */
export async function getCentrifugoToken() {
  try {
    const res = await apiInterceptor.get(environment('VUE_APP_API_URL') + CENTRIFUGO_TOKEN_PATH)
    const token = res?.token ?? res?.data?.token ?? ''
    if (token) return token
    console.warn('[Centrifugo] Token endpoint returned no token.')
  } catch (error) {
    console.error('[Centrifugo] Failed to fetch connection token:', error?.message || error)
  }

  // ── Local-dev fallback: static pre-generated token (no refresh) ──────────
  // Used only when the API is unreachable or the session lacks livechat
  // permission. Never set VUE_APP_CENTRIFUGO_DEV_TOKEN in staging/production.
  const devToken = environment('VUE_APP_CENTRIFUGO_DEV_TOKEN')
  if (devToken) return devToken

  return ''
}
